import { formatDuration } from './format'
import type {
  ActivityItem,
  AgentPart,
  CodePanel,
  TableData,
  ToolActivity,
} from '@/types/chat'
import type {
  BlockType,
  DisplayContent,
  StreamBlockPayload,
  ToolStatus,
} from '@/types/stream'

/**
 * blocks → AgentPart[] — the ONE mapping from the stream's block model to what
 * a turn renders. `useChatStream` feeds it live blocks and `lib/adapters` feeds
 * it persisted `content_blocks`, so an in-flight turn and the same turn after a
 * refetch are byte-identical (they used to be two hand-mirrored mappings).
 *
 * Order is preserved: each run of consecutive thinking/tool blocks becomes one
 * `activity` part WHERE IT OCCURRED (collapsed to a line by AgentActivity),
 * with prose keeping its own place around it.
 *
 * The split: an activity run owns the *trace* — thinking text, one row per
 * tool with its live status/label/duration, the code it streamed, and its
 * result table. SQL and its data stay collapsed inside that same tool row
 * (user 2026-08-04) rather than graduating to always-open cards in the answer
 * body. Files come from the turn's artifacts, never from a tool's
 * file_preview, so they land once.
 */

const IN_FLIGHT: ToolStatus[] = ['streaming', 'pending', 'running']
const HIDDEN_TOOLS = new Set(['visualize'])

export interface TurnBlock {
  type: BlockType
  /** Accumulated text (text blocks) or reasoning (thinking blocks). */
  text: string
  /** Accumulated `input_partial` — raw arg JSON, possibly incomplete. */
  partialJson: string
  /** Accumulated `display_content_delta` — the live code/SQL panel. */
  displayText: string
  /** False once `block_stop` lands. Tool blocks stay open while executing. */
  open: boolean
  /** `block_start`'s payload with every `block_update` merged onto it. */
  data: StreamBlockPayload
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined
const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined
const obj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined

const toolIdOf = (data: StreamBlockPayload) => data.tool_use_id ?? data.id ?? ''
const toolNameOf = (data: StreamBlockPayload) =>
  data.name ?? data.tool_name ?? 'tool'

/** Parsed args — the block_update replacement, else the raw partial JSON. */
function inputOf(block: TurnBlock): Record<string, unknown> | undefined {
  const input = obj(block.data.input)
  if (input && Object.keys(input).length > 0) return input
  if (!block.partialJson) return undefined
  try {
    return obj(JSON.parse(block.partialJson))
  } catch {
    return undefined // still streaming — not parseable yet, by contract
  }
}

/** `description` is declared FIRST in every tool's schema specifically so it
 * streams in before long fields like `code`/`sql_query` (tools/base.py) —
 * but `input_partial` isn't valid JSON until the whole call closes, so
 * `inputOf` can't see it yet. Regex it out of the raw fragment the moment
 * its closing quote arrives, so the row's label shows the real description
 * immediately instead of a generic "Running X" placeholder for however long
 * the rest of the arguments take to stream. */
const _DESCRIPTION_RE = /"description"\s*:\s*"((?:[^"\\]|\\.)*)"/
function earlyDescriptionOf(block: TurnBlock): string | undefined {
  const m = _DESCRIPTION_RE.exec(block.partialJson)
  if (!m) return undefined
  try {
    return JSON.parse(`"${m[1]}"`) as string
  } catch {
    return undefined
  }
}

const FALLBACK_LANG: Record<string, string> = {
  code_execution: 'python',
  Bash: 'bash',
}
function codePanelOf(block: TurnBlock): CodePanel | undefined {
  const name = toolNameOf(block.data)
  const fallbackLang = FALLBACK_LANG[name] ?? 'sql'

  // query_engine_id/type live only in the tool call's args (never in
  // display_content) — read them once and attach to whichever branch below
  // produces the code, so Run always knows which DB to hit.
  const input = inputOf(block)
  const engine =
    name === 'execute_sql'
      ? {
          queryEngineId: str(input?.query_engine_id),
          queryEngineType: str(input?.query_engine_type),
        }
      : undefined

  // The live stream wins — it is what makes the tool watchable in flight.
  if (block.displayText)
    return { language: fallbackLang, code: block.displayText, ...engine }

  const display = block.data.display_content
  if (display?.type === 'json_block') {
    try {
      const parsed = obj(JSON.parse(display.json_block))
      const code = str(parsed?.code)
      if (code)
        return {
          language: str(parsed?.language) ?? fallbackLang,
          code,
          title: str(parsed?.title),
          ...engine,
        }
    } catch {
      // malformed json_block — fall through to the parsed input below
    }
  }

  const code =
    str(input?.sql_query) ??
    str(input?.sql) ??
    str(input?.code) ??
    str(input?.command)
  return code ? { language: fallbackLang, code, ...engine } : undefined
}

/** Columns whose every populated preview cell reads as a number. */
function numericColumnsOf(
  columns: string[],
  rows: (string | number)[][],
): string[] | undefined {
  if (rows.length === 0) return undefined
  const numeric = columns.filter((_, j) =>
    rows.some((row) => row[j] !== undefined && String(row[j]).trim() !== '')
      ? rows.every((row) => {
          const cell = String(row[j] ?? '').trim()
          return cell === '' || /^[+−-]?[\d,]+(\.\d+)?$/.test(cell)
        })
      : false,
  )
  return numeric.length ? numeric : undefined
}

/** "486 rows · 6.1 s", or "2 of 486 rows · 6.1 s" for a truncated preview. */
function datasetMeta(
  display: Extract<DisplayContent, { type: 'query_dataset' }>,
  shown: number,
): string | undefined {
  const bits: string[] = []
  const total = display.total_rows
  if (total !== undefined) {
    const totalText = total.toLocaleString('en-US')
    bits.push(
      display.truncated && shown > 0 && shown < total
        ? `${shown} of ${totalText} rows`
        : `${totalText} rows`,
    )
  }
  if (display.duration_seconds !== undefined)
    bits.push(`${display.duration_seconds} s`)
  return bits.length ? bits.join(' · ') : undefined
}

/** A tool_result's table: the wire's `query_dataset`, or the legacy
 * `display:{kind:'table'}` / flat `{columns, rows}` fixture shape. */
function tableOf(result: TurnBlock): TableData | null {
  const display = result.data.display_content
  if (display?.type === 'query_dataset' && display.columns) {
    const rows: (string | number)[][] = display.preview ?? []
    return {
      columns: display.columns,
      rows,
      numericColumns: numericColumnsOf(display.columns, rows),
      meta: datasetMeta(display, rows.length),
    }
  }
  const raw = result.data as unknown as Record<string, unknown>
  const legacy = obj(raw.display) ?? raw
  if (!Array.isArray(legacy.columns) || !Array.isArray(legacy.rows)) return null
  return {
    columns: legacy.columns as string[],
    numericColumns: Array.isArray(legacy.numeric_columns)
      ? (legacy.numeric_columns as string[])
      : undefined,
    rows: legacy.rows as (string | number)[][],
    meta: str(legacy.meta),
  }
}

function resultLineOf(result: TurnBlock | undefined): string | undefined {
  if (!result) return undefined
  const display = result.data.display_content
  if (display?.type === 'query_dataset')
    return datasetMeta(display, display.preview?.length ?? 0)
  if (display?.type === 'file_preview') {
    const names = (display.files ?? []).map((f) => f.filename).filter(Boolean)
    return names.length ? names.join(', ') : undefined
  }
  // Legacy fixtures carry the metrics as flat fields instead.
  const raw = result.data as unknown as Record<string, unknown>
  const bits: string[] = []
  const ms = num(raw.duration_ms)
  if (ms !== undefined) bits.push(`${ms / 1000} s`)
  const rows = num(raw.rows)
  if (rows !== undefined) bits.push(`${rows} rows`)
  const source = str(raw.source)
  if (source) bits.push(source)
  return bits.length ? bits.join(' · ') : undefined
}

function outputOf(result: TurnBlock | undefined): string | undefined {
  const display = result?.data.display_content
  return display?.type === 'tool_output' ? display.text : undefined
}

function grepInputOf(input: Record<string, unknown>): string {
  const flag = input['-i'] ? '-i ' : ''
  const pattern = str(input.pattern) ?? ''
  const path = str(input.path)
  return `grep ${flag}"${pattern}"${path ? ` ${path}` : ''}`
}

function inputTextOf(block: TurnBlock): string | undefined {
  const input = inputOf(block)
  if (!input) return undefined
  if (toolNameOf(block.data) === 'Grep') return grepInputOf(input)
  const show = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v))
  const entries = Object.entries(input).filter(([k]) => k !== 'description')
  if (entries.length === 0) return undefined
  if (entries.length === 1) return show(entries[0][1])
  return entries.map(([k, v]) => `${k}: ${show(v)}`).join('\n')
}

function toolActivityOf(
  block: TurnBlock,
  result: TurnBlock | undefined,
): ToolActivity {
  const { data } = block
  const toolName = toolNameOf(data)
  const failed = data.status === 'error' || result?.data.is_error === true
  const status: ToolStatus =
    data.status ?? (failed ? 'error' : block.open ? 'running' : 'complete')
  const liveDescription = inputOf(block) ? undefined : earlyDescriptionOf(block)
  const code = codePanelOf(block)
  return {
    kind: 'tool',
    id: toolIdOf(data) || `${toolName}-${block.data.start_timestamp ?? ''}`,
    toolName,
    label:
      liveDescription ??
      data.message ??
      result?.data.message ??
      `Ran ${toolName}`,
    status: failed ? 'error' : status,
    durationMs: data.duration_ms ?? data.duration,
    code,
    inputText: code ? undefined : inputTextOf(block),
    result: failed ? undefined : resultLineOf(result),
    table: failed || !result ? undefined : (tableOf(result) ?? undefined),
    output: outputOf(result),
    failed: failed || undefined,
  }
}

/** Elapsed time for one run of steps: the timestamp span when the wire carries
 * timestamps, else the sum of the step durations. */
function elapsedMs(blocks: TurnBlock[], items: ActivityItem[]): number {
  const stamps = blocks.flatMap((b) =>
    [b.data.start_timestamp, b.data.stop_timestamp]
      .map((s) => (s ? Date.parse(s) : NaN))
      .filter((n) => !Number.isNaN(n)),
  )
  const span =
    stamps.length >= 2 ? Math.max(...stamps) - Math.min(...stamps) : 0
  const sum = items.reduce((total, item) => total + (item.durationMs ?? 0), 0)
  // Take the larger: only SOME blocks may carry timestamps (the span then
  // under-reports), and parallel tools make the sum over-report — but a run
  // is never shorter than its longest-known measure either way.
  return Math.max(span, sum)
}

/** First sentence of a thinking block, for its collapsed one-liner. */
export function firstSentence(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  const end = flat.search(/[.!?](\s|$)/)
  const head = end > 0 ? flat.slice(0, end) : flat
  return head.length > 120 ? `${head.slice(0, 119)}…` : head
}

function summaryOf(
  items: ActivityItem[],
  blocks: TurnBlock[],
  live: boolean,
): { summary: string; meta?: string } {
  const inFlight = [...items]
    .reverse()
    .find((item) =>
      item.kind === 'thinking' ? item.running : IN_FLIGHT.includes(item.status),
    )
  if (live) {
    const current = inFlight ?? items.at(-1)
    if (current)
      return {
        summary:
          current.kind === 'thinking'
            ? firstSentence(current.text) || 'Thinking'
            : current.label,
      }
  }
  if (items.length === 1) {
    const [only] = items
    const meta =
      only.durationMs !== undefined
        ? formatDuration(only.durationMs)
        : undefined
    if (only.kind === 'thinking') {
      const head = firstSentence(only.text)
      return head
        ? { summary: head, meta }
        : { summary: meta ? `Thought for ${meta}` : 'Thought' }
    }
    return { summary: only.label, meta }
  }
  const count = `${items.length} step${items.length === 1 ? '' : 's'}`
  const ms = elapsedMs(blocks, items)
  return {
    summary:
      ms > 0
        ? `Worked for ${formatDuration(ms)} · ${count}`
        : `Worked · ${count}`,
  }
}

export interface DerivedTurn {
  parts: AgentPart[]
  /** An open text block is streaming — the caret rides its markdown part. */
  streamingText: boolean
}

/** An activity part under construction, plus the blocks that fed it. */
interface OpenGroup {
  part: Extract<AgentPart, { kind: 'activity' }>
  blocks: TurnBlock[]
}

/**
 * @param live true while the turn is still streaming — a group's line then
 * tracks its current step instead of reporting a total. Persisted history is
 * never live; `useChatStream` passes false once the terminal stop lands.
 */
export function deriveTurn(blocks: TurnBlock[], live = false): DerivedTurn {
  const parts: AgentPart[] = []
  const groups: OpenGroup[] = []
  let streamingText = false
  let openIdx = -1
  const current = () => (openIdx >= 0 ? groups[openIdx] : null)
  const intoGroup = (): OpenGroup => {
    const open = current()
    if (open) return open
    const next: OpenGroup = {
      part: { kind: 'activity', items: [], summary: '', running: false },
      blocks: [],
    }
    groups.push(next)
    parts.push(next.part)
    openIdx = groups.length - 1
    return next
  }

  const resultFor = (block: TurnBlock) => {
    const id = toolIdOf(block.data)
    return id
      ? blocks.find((b) => b.type === 'tool_result' && toolIdOf(b.data) === id)
      : undefined
  }

  for (const block of blocks) {
    const partsBefore = parts.length
    switch (block.type) {
      case 'thinking': {
        // A block that opened before its first delta still gets a row, so the
        // line appears the moment the model starts reasoning.
        if (!block.text && !block.open) break
        const current = intoGroup()
        current.blocks.push(block)
        current.part.items.push({
          kind: 'thinking',
          text: block.text,
          running: block.open,
          durationMs: block.data.duration,
        })
        if (block.open) current.part.running = true
        break
      }
      case 'tool_use': {
        if (HIDDEN_TOOLS.has(toolNameOf(block.data))) break
        const result = resultFor(block)
        const tool = toolActivityOf(block, result)
        const group = intoGroup()
        group.blocks.push(block)
        group.part.items.push(tool)
        if (IN_FLIGHT.includes(tool.status)) group.part.running = true
        break
      }
      case 'tool_result':
        // Folded into its tool_use — row label, result line, and table alike.
        current()?.blocks.push(block)
        break
      case 'text': {
        if (!block.text.trim() && !block.open) break
        parts.push({ kind: 'markdown', text: block.text })
        if (block.open) streamingText = true
        break
      }
    }
    // Body content landed → whatever comes next starts its own group.
    if (parts.length > partsBefore && parts.at(-1) !== current()?.part)
      openIdx = -1
  }

  for (const { part, blocks: own } of groups) {
    Object.assign(part, summaryOf(part.items, own, live && part.running))
  }
  return { parts, streamingText }
}

/** A closed block from persisted history (`content_blocks`, loosely typed by
 * the spec: `additionalProperties: true`). */
export function historyBlock(raw: Record<string, unknown>): TurnBlock {
  const type = (str(raw.type) ?? 'text') as BlockType
  return {
    type,
    text: str(raw.thinking) ?? str(raw.text) ?? '',
    partialJson: '',
    displayText: '',
    open: false,
    data: { ...raw, type } as unknown as StreamBlockPayload,
  }
}
